import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import UserProfileModal from "./UserProfileModal";
import {
  Users,
  UserPlus,
  Search,
  Check,
  X,
  User,
  Loader2,
  BarChart3,
  Lock,
  Globe,
  UserCheck,
  Clock,
  Trash2,
  Send
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const FriendsTab = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // User Profile Modal
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const fetchFriends = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/friends`);
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error("Failed to fetch friends:", error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const [incoming, outgoing] = await Promise.all([
        axios.get(`${API_URL}/api/friends/requests/incoming`),
        axios.get(`${API_URL}/api/friends/requests/outgoing`)
      ]);
      setIncomingRequests(incoming.data.requests || []);
      setOutgoingRequests(outgoing.data.requests || []);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFriends(), fetchRequests()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFriends, fetchRequests]);

  // Search users
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(searchQuery)}`);
        // Filter out existing friends and self
        const filtered = (response.data.users || []).filter(
          u => u.id !== user?.id && !friends.some(f => f.user_id === u.id)
        );
        setSearchResults(filtered);
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, user?.id, friends]);

  const sendFriendRequest = async (recipientId) => {
    try {
      await axios.post(`${API_URL}/api/friends/request`, {
        recipient_id: recipientId
      });
      toast.success("Friend request sent!");
      setSearchQuery("");
      setSearchResults([]);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    }
  };

  const respondToRequest = async (requestId, action) => {
    try {
      await axios.post(`${API_URL}/api/friends/requests/${requestId}/respond`, {
        action
      });
      toast.success(action === "accept" ? "Friend request accepted!" : "Friend request rejected");
      fetchFriends();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond to request");
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API_URL}/api/friends/requests/${requestId}`);
      toast.success("Friend request cancelled");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm("Are you sure you want to remove this friend?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/friends/${friendId}`);
      toast.success("Friend removed");
      fetchFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove friend");
    }
  };

  const viewFriendProfile = (friend) => {
    setSelectedUserId(friend.user_id);
    setProfileModalOpen(true);
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case "public": return <Globe className="w-3 h-3 text-green-400" />;
      case "friends_only": return <UserCheck className="w-3 h-3 text-blue-400" />;
      case "private": return <Lock className="w-3 h-3 text-red-400" />;
      default: return <Globe className="w-3 h-3 text-green-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search to Add Friends */}
      <Card className="bg-card/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4" />
            Add Friends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-3 space-y-2">
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50"
                >
                  <div className="flex items-center gap-3">
                    {result.avatar ? (
                      <img
                        src={result.avatar}
                        alt={result.username}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="font-medium">{result.username}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => sendFriendRequest(result.id)}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Request
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Friends/Requests */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="friends" className="gap-1">
            <Users className="w-4 h-4" />
            Friends ({friends.length})
          </TabsTrigger>
          <TabsTrigger value="incoming" className="gap-1">
            <UserPlus className="w-4 h-4" />
            Incoming ({incomingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="gap-1">
            <Clock className="w-4 h-4" />
            Pending ({outgoingRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* Friends List */}
        <TabsContent value="friends" className="mt-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              {friends.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No friends yet</p>
                  <p className="text-sm">Search for users above to add friends</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {friends.map((friend) => (
                      <div
                        key={friend.user_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50"
                      >
                        <button
                          onClick={() => viewFriendProfile(friend)}
                          className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
                        >
                          {friend.avatar ? (
                            <img
                              src={friend.avatar}
                              alt={friend.username}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{friend.username}</span>
                              {friend.is_online && (
                                <span className="w-2 h-2 bg-green-500 rounded-full" title="Online" />
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              {getPrivacyIcon(friend.profile_privacy)}
                              <span className="capitalize">{friend.profile_privacy?.replace("_", " ") || "public"}</span>
                            </div>
                          </div>
                        </button>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewFriendProfile(friend)}
                            disabled={friend.profile_privacy === "private"}
                            title={friend.profile_privacy === "private" ? "Stats are private" : "View profile & stats"}
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeFriend(friend.user_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incoming Requests */}
        <TabsContent value="incoming" className="mt-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              {incomingRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No incoming friend requests</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {incomingRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50"
                    >
                      <div className="flex items-center gap-3">
                        {request.requester_avatar ? (
                          <img
                            src={request.requester_avatar}
                            alt={request.requester_username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <span className="font-medium">{request.requester_username}</span>
                          {request.message && (
                            <p className="text-xs text-muted-foreground">{request.message}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => respondToRequest(request.id, "accept")}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => respondToRequest(request.id, "reject")}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outgoing Requests */}
        <TabsContent value="outgoing" className="mt-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              {outgoingRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending friend requests</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {outgoingRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50"
                    >
                      <div className="flex items-center gap-3">
                        {request.recipient_avatar ? (
                          <img
                            src={request.recipient_avatar}
                            alt={request.recipient_username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <span className="font-medium">{request.recipient_username}</span>
                          <p className="text-xs text-muted-foreground">Waiting for response...</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => cancelRequest(request.id)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Profile Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
        userId={selectedUserId}
      />
    </div>
  );
};

export default FriendsTab;
